/**
 * audience-nav.ts
 * ─────────────────────────────────────────────────────────────────
 * Navigation contextuelle adaptée par profil utilisateur.
 *
 * Source unique pour :
 *  - les quick-links sectoriels intégrés au Header (1 seule barre, plus de "double header")
 *  - les liens prioritaires du Footer adaptatif
 *  - le CTA "contact rapide" de chaque audience
 *
 * Permet à l'utilisateur d'avoir une expérience où l'ensemble du site
 * (header + footer) parle son langage métier sur l'ensemble du parcours.
 */

import type { AudienceId } from '@shared/constants/audiences';

export interface AudienceNavLink {
  label: string;
  href: string;
  iconName: string;
  badge?: string;
  /**
   * Description courte (15-40 caractères idéal) affichée sous le label
   * dans les mega-menus du Header desktop. Ignoré pour les liens simples
   * sans children ni megaMenu.
   */
  description?: string;
  /**
   * Sous-menu en LISTE (1 colonne) — version simple "linkbar".
   * Le `href` du parent reste cliquable.
   */
  children?: AudienceNavLink[];
  /**
   * Sous-menu en MEGA-MENU (plusieurs colonnes thématiques),
   * inspiré du pattern Effy / Hellio :
   *
   *   ISOLATION       CHAUFFAGE         RÉNOVATION GLOBALE
   *   • Combles       • Pompe à chaleur • Audit énergétique
   *   • Murs          • Poêle           • Calculer mes aides
   *   • Fenêtres      • Chauffe-eau…
   *
   * Si `megaMenu` ET `children` sont définis, `megaMenu` a priorité.
   */
  megaMenu?: AudienceNavMenuGroup[];
}

export interface AudienceNavMenuGroup {
  /** Titre de colonne (affiché en uppercase tracking-wide). */
  title: string;
  /** Liens de la colonne (icône + label, optionnellement description). */
  items: AudienceNavLink[];
}

export interface AudienceFooterCta {
  label: string;
  href: string;
  description: string;
  variant?: 'primary' | 'secondary' | 'highlight';
}

export interface AudienceNavConfig {
  /** Liens contextuels affichés dans la barre intégrée au Header */
  quickLinks: AudienceNavLink[];
  /** CTA prioritaire affiché à droite du Header sur cette audience */
  primaryCta: { label: string; href: string };
  /** CTAs personnalisés affichés dans la sidebar "Contact rapide" du Footer */
  footerCtas: AudienceFooterCta[];
  /** Court paragraphe contextuel ajouté en intro du Footer */
  footerLeadIn: string;
  /** Affiche le footerLeadIn en italique (utile pour une devise / slogan) */
  footerLeadInItalic?: boolean;
}

const PARTICULIERS: AudienceNavConfig = {
  quickLinks: [
    { label: 'Accueil', href: '/particuliers', iconName: 'home' },
    {
      label: 'Travaux',
      href: '/particuliers/travaux',
      iconName: 'wrench',
      description: 'Opérations courantes éligibles aux primes',
      megaMenu: [
        {
          title: 'Isolation',
          items: [
            { label: 'Combles', href: '/particuliers/travaux/isolation-combles', iconName: 'home' },
            {
              label: 'Murs',
              href: '/particuliers/travaux/isolation-murs',
              iconName: 'shield-check',
            },
            {
              label: 'Fenêtres',
              href: '/particuliers/travaux/isolation-fenetres',
              iconName: 'layers',
            },
            { label: 'Sols', href: '/particuliers/travaux/isolation-sols', iconName: 'layers' },
          ],
        },
        {
          title: 'Chauffage & eau chaude',
          items: [
            {
              label: 'Pompe à chaleur',
              href: '/particuliers/travaux/pompe-a-chaleur',
              iconName: 'snowflake',
            },
            {
              label: 'Poêle à granulés',
              href: '/particuliers/travaux/poele-a-granules',
              iconName: 'flame',
            },
            {
              label: 'Chauffe-eau thermodynamique',
              href: '/particuliers/travaux/chauffe-eau-thermodynamique',
              iconName: 'droplet',
            },
            {
              label: 'Chauffe-eau solaire',
              href: '/particuliers/travaux/chauffe-eau-solaire',
              iconName: 'sun',
            },
            {
              label: 'VMC double flux',
              href: '/particuliers/travaux/vmc-double-flux',
              iconName: 'wind',
            },
          ],
        },
        {
          title: 'Rénovation globale',
          items: [
            {
              label: 'Bilan énergétique',
              href: '/particuliers/bilan-energetique',
              iconName: 'gauge',
            },
            {
              label: 'Audit énergétique',
              href: '/particuliers/audit-energetique',
              iconName: 'file-search',
            },
            {
              label: 'Rénovation globale',
              href: '/particuliers/renovation-globale',
              iconName: 'hammer',
            },
            {
              label: "Mon Accompagnateur Rénov'",
              href: '/particuliers/mon-accompagnateur-renov',
              iconName: 'users',
            },
          ],
        },
      ],
    },
    {
      label: 'Aides',
      href: '/particuliers/aides',
      iconName: 'coins',
      description: 'MaPrimeRénov’, CEE, éco-PTZ, TVA…',
      megaMenu: [
        {
          title: '',
          items: [
            {
              label: 'MaPrimeRénov’',
              href: '/particuliers/aides/maprimerenov',
              iconName: 'piggy-bank',
            },
            { label: 'Prime énergie (CEE)', href: '/particuliers/aides/cee', iconName: 'sparkles' },
            {
              label: 'Coup de pouce',
              href: '/particuliers/aides/coup-de-pouce',
              iconName: 'flame',
            },
            { label: 'Éco-PTZ', href: '/particuliers/aides/eco-ptz', iconName: 'banknote' },
            { label: 'TVA à 5,5 %', href: '/particuliers/aides/tva-reduite', iconName: 'percent' },
          ],
        },
      ],
    },
    { label: 'Actu', href: '/blog', iconName: 'newspaper' },
  ],
  primaryCta: { label: 'Calculer mes aides', href: '/particuliers/calculer-mes-aides' },
  footerCtas: [
    {
      label: 'Calculer mes aides',
      href: '/particuliers/calculer-mes-aides',
      description: 'Toutes vos aides en 2 minutes',
      variant: 'primary',
    },
    {
      label: 'Être rappelé(e)',
      href: '/contact?source=particulier',
      description: 'Sous 24h, sans engagement',
      variant: 'secondary',
    },
  ],
  footerLeadIn: 'On vous aide à mieux vivre chez vous, sans vous ruiner.',
};

const PROFESSIONNELS: AudienceNavConfig = {
  quickLinks: [
    { label: 'Accueil', href: '/professionnels', iconName: 'building-2' },
    {
      label: 'Travaux pro',
      href: '/professionnels/travaux',
      iconName: 'wrench',
      description: 'Travaux de vos locaux éligibles aux primes CEE',
      megaMenu: [
        {
          title: 'Chauffage & climatisation',
          items: [
            {
              label: 'Pompe à chaleur',
              href: '/professionnels/travaux/pac-tertiaire',
              iconName: 'snowflake',
            },
            {
              label: 'Ventilation double flux',
              href: '/professionnels/travaux/cta-double-flux',
              iconName: 'wind',
            },
          ],
        },
        {
          title: 'Enveloppe & éclairage',
          items: [
            {
              label: 'Isolation toiture-terrasse',
              href: '/professionnels/travaux/isolation-toiture-terrasse',
              iconName: 'home',
            },
            {
              label: 'Éclairage LED',
              href: '/professionnels/travaux/relamping-led',
              iconName: 'sparkles',
            },
            {
              label: 'Isolation des réseaux d’eau chaude',
              href: '/professionnels/travaux/calorifugeage',
              iconName: 'shield-check',
            },
          ],
        },
        {
          title: 'Pilotage énergie',
          items: [
            {
              label: 'Pilotage intelligent du bâtiment (GTB)',
              href: '/professionnels/travaux/gtb',
              iconName: 'gauge',
            },
          ],
        },
      ],
    },
    {
      label: 'Aides',
      href: '/professionnels/aides',
      iconName: 'coins',
      description: 'Primes CEE, fiscalité énergie, prêts climat',
      megaMenu: [
        {
          title: 'Primes & financements',
          items: [
            {
              label: 'Primes CEE locaux pro',
              href: '/professionnels/aides/cee-bat',
              iconName: 'sparkles',
            },
            {
              label: 'Coup de pouce chauffage & clim',
              href: '/professionnels/aides/coup-de-pouce-cvc',
              iconName: 'flame',
            },
            {
              label: 'Suramortissement fiscal',
              href: '/professionnels/aides/suramortissement',
              iconName: 'banknote',
            },
            {
              label: 'Prêts Bpifrance climat',
              href: '/professionnels/aides/prets-bpifrance',
              iconName: 'piggy-bank',
            },
          ],
        },
        {
          title: 'Optimiser la facture',
          items: [
            {
              label: 'Optimisation TURPE (acheminement)',
              href: '/professionnels/aides/optimisation-turpe',
              iconName: 'trending-down',
            },
            {
              label: 'Exonération CSPE (accise élec)',
              href: '/professionnels/aides/exoneration-cspe',
              iconName: 'percent',
            },
          ],
        },
      ],
    },
  ],
  primaryCta: { label: 'Comparer mes offres', href: '/professionnels/comparateur-energie' },
  footerCtas: [
    {
      label: 'Audit factures gratuit',
      href: '/professionnels/comparateur-energie',
      description: 'Économies possibles, offres comparées',
      variant: 'primary',
    },
    {
      label: 'Diagnostic CEE',
      href: '/professionnels/diagnostic-cee',
      description: 'Estimation primes en 60 s',
      variant: 'secondary',
    },
  ],
  footerLeadIn: 'On compare toutes les offres, on négocie pour vous. Gratuitement.',
};

const ENTREPRISES: AudienceNavConfig = {
  quickLinks: [
    { label: 'Accueil', href: '/entreprises', iconName: 'factory' },
    {
      label: 'Patrimoine',
      href: '/entreprises',
      iconName: 'building-2',
      description: 'Tertiaire, industriel, multi-sites',
      megaMenu: [
        {
          title: 'Tertiaire (bureaux & ERP)',
          items: [
            {
              label: 'Décret Tertiaire (OPERAT)',
              href: '/tertiaire/decret-tertiaire',
              iconName: 'gauge',
            },
            {
              label: 'Pilotage du bâtiment (GTB)',
              href: '/tertiaire/travaux/gtb',
              iconName: 'cpu',
            },
            { label: 'Plan -40 % 2030', href: '/tertiaire', iconName: 'trending-down' },
          ],
        },
        {
          title: 'Industrie',
          items: [
            {
              label: 'Audit énergétique réglementaire',
              href: '/industrie/operations/audit-reglementaire',
              iconName: 'file-search',
            },
            {
              label: 'Primes CEE industrielles',
              href: '/entreprises/aides/cee-ind',
              iconName: 'cog',
            },
            {
              label: 'Récupération de chaleur',
              href: '/industrie/operations/recuperation-chaleur',
              iconName: 'flame',
            },
            {
              label: 'Certification ISO 50001',
              href: '/industrie/iso-50001',
              iconName: 'shield-check',
            },
          ],
        },
        {
          title: 'Multi-sites & flotte',
          items: [
            {
              label: 'Courtage électricité & gaz',
              href: '/entreprises/comparateur-energie',
              iconName: 'git-compare-arrows',
            },
            { label: 'Diagnostic CEE', href: '/entreprises/diagnostic-cee', iconName: 'zap' },
            {
              label: 'Reporting consolidé',
              href: '/contact?source=entreprise&besoin=reporting',
              iconName: 'clipboard-check',
            },
          ],
        },
      ],
    },
    {
      label: 'Aides',
      href: '/entreprises/aides',
      iconName: 'coins',
      description: 'Primes CEE, Fonds chaleur, fiscalité énergie',
      megaMenu: [
        {
          title: 'Subventions & primes',
          items: [
            {
              label: 'Primes CEE bâtiments',
              href: '/entreprises/aides/cee-bat',
              iconName: 'sparkles',
            },
            { label: 'Primes CEE industrie', href: '/entreprises/aides/cee-ind', iconName: 'cog' },
            {
              label: 'Fonds chaleur ADEME',
              href: '/entreprises/aides/fonds-chaleur',
              iconName: 'flame',
            },
            {
              label: 'Fonds décarbonation',
              href: '/entreprises/aides/fonds-decarbonation',
              iconName: 'banknote',
            },
          ],
        },
        {
          title: 'Fiscalité & facture',
          items: [
            {
              label: 'Suramortissement fiscal',
              href: '/entreprises/aides/suramortissement',
              iconName: 'piggy-bank',
            },
            {
              label: 'Optimisation TURPE multi-sites',
              href: '/entreprises/aides/optimisation-turpe',
              iconName: 'trending-down',
            },
            {
              label: 'Exonération CSPE (accise élec)',
              href: '/entreprises/aides/exoneration-cspe',
              iconName: 'percent',
            },
          ],
        },
      ],
    },
  ],
  primaryCta: { label: 'Demander une étude', href: '/contact?source=entreprise' },
  footerCtas: [
    {
      label: 'Demander une étude chiffrée',
      href: '/contact?source=entreprise',
      description: 'Référent dédié, confidentialité garantie',
      variant: 'primary',
    },
    {
      label: 'Audit obligation tertiaire',
      href: '/entreprises/decret-tertiaire',
      description: 'Trajectoire -40 % chiffrée',
      variant: 'secondary',
    },
  ],
  footerLeadIn:
    'Un référent dédié pour tous vos sites. Premiers résultats sous 15 jours. Confidentialité garantie.',
};

const TERTIAIRE: AudienceNavConfig = {
  quickLinks: [
    { label: 'Accueil', href: '/tertiaire', iconName: 'briefcase' },
    {
      label: 'Conformité',
      href: '/tertiaire/decret-tertiaire',
      iconName: 'shield-check',
      description: 'Décret Tertiaire, Décret BACS',
      megaMenu: [
        {
          title: 'Décret Tertiaire',
          items: [
            {
              label: 'Trajectoire -40 % 2030',
              href: '/tertiaire/decret-tertiaire',
              iconName: 'gauge',
            },
            { label: 'Simulateur OPERAT', href: '/decret-tertiaire', iconName: 'calendar' },
            {
              label: 'Diagnostic CEE tertiaire',
              href: '/tertiaire/diagnostic-cee',
              iconName: 'clipboard-check',
            },
          ],
        },
        {
          title: 'Décret BACS',
          items: [
            { label: 'Audit GTB existant', href: '/tertiaire/travaux/gtb', iconName: 'cpu' },
            {
              label: 'Financer sa GTB avec les CEE',
              href: '/tertiaire/aides/cee-bat',
              iconName: 'gauge',
            },
          ],
        },
      ],
    },
    {
      label: 'Travaux',
      href: '/tertiaire/travaux',
      iconName: 'wrench',
      description: 'Travaux financés par les primes CEE',
      megaMenu: [
        {
          title: 'Chauffage & climatisation',
          items: [
            {
              label: 'PAC tertiaire',
              href: '/tertiaire/travaux/pac-tertiaire',
              iconName: 'snowflake',
            },
            { label: 'GTB pilotage', href: '/tertiaire/travaux/gtb', iconName: 'cpu' },
            {
              label: 'ECS solaire collective',
              href: '/tertiaire/travaux/ecs-solaire',
              iconName: 'sun',
            },
          ],
        },
        {
          title: 'Enveloppe & éclairage',
          items: [
            {
              label: 'Isolation toiture-terrasse',
              href: '/tertiaire/travaux/isolation-toiture',
              iconName: 'home',
            },
            {
              label: 'Relamping LED',
              href: '/tertiaire/travaux/relamping-led',
              iconName: 'sparkles',
            },
            {
              label: 'Calorifugeage',
              href: '/tertiaire/travaux/calorifugeage',
              iconName: 'shield-check',
            },
          ],
        },
      ],
    },
    {
      label: 'Aides',
      href: '/tertiaire/aides',
      iconName: 'coins',
      description: 'Primes CEE, Coups de pouce, fiscalité énergie',
      megaMenu: [
        {
          title: '',
          items: [
            {
              label: 'Primes CEE tertiaires',
              href: '/tertiaire/aides/cee-bat',
              iconName: 'sparkles',
            },
            {
              label: 'Coup de pouce chauffage & clim',
              href: '/tertiaire/aides/coup-de-pouce-cvc',
              iconName: 'flame',
            },
            {
              label: 'Suramortissement art. 39',
              href: '/tertiaire/aides/suramortissement',
              iconName: 'banknote',
            },
            {
              label: 'Prêts Bpifrance climat',
              href: '/tertiaire/aides/prets-bpifrance',
              iconName: 'piggy-bank',
            },
            {
              label: 'Optimisation TURPE (acheminement)',
              href: '/tertiaire/aides/optimisation-turpe',
              iconName: 'trending-down',
            },
          ],
        },
      ],
    },
  ],
  primaryCta: { label: 'Audit obligation tertiaire', href: '/tertiaire/decret-tertiaire' },
  footerCtas: [
    {
      label: 'Audit obligation tertiaire',
      href: '/tertiaire/decret-tertiaire',
      description: 'Plan de réduction chiffré, déclaration incluse',
      variant: 'primary',
    },
    {
      label: 'Prendre rendez-vous',
      href: '/contact?source=tertiaire',
      description: 'Référent dédié, confidentialité garantie',
      variant: 'secondary',
    },
  ],
  footerLeadIn:
    "Votre obligation de réduire la consommation de 40 % d'ici 2030, pilotée sans stress. Travaux financés, déclaration incluse.",
};

const INDUSTRIE: AudienceNavConfig = {
  quickLinks: [
    { label: 'Accueil', href: '/industrie', iconName: 'cog' },
    {
      label: 'Utilités & process',
      href: '/industrie/operations',
      iconName: 'wrench',
      description: 'Moteurs, air comprimé, chaleur, bâti industriel',
      megaMenu: [
        {
          title: 'Utilités',
          items: [
            {
              label: 'Variateurs de vitesse',
              href: '/industrie/operations/variateurs',
              iconName: 'zap',
            },
            {
              label: 'Moteurs IE3/IE4',
              href: '/industrie/operations/moteurs-haute-efficacite',
              iconName: 'cog',
            },
            { label: 'Air comprimé', href: '/industrie/operations/air-comprime', iconName: 'wind' },
            {
              label: 'Récupération chaleur fatale',
              href: '/industrie/operations/recuperation-chaleur',
              iconName: 'flame',
            },
          ],
        },
        {
          title: 'Bâti industriel',
          items: [
            {
              label: 'Isolation calorifuge',
              href: '/industrie/operations/calorifugeage',
              iconName: 'shield-check',
            },
            {
              label: 'Optimisation chaufferie',
              href: '/industrie/operations/chaufferie',
              iconName: 'flame',
            },
            {
              label: 'Éclairage industriel',
              href: '/industrie/operations/eclairage',
              iconName: 'sparkles',
            },
          ],
        },
        {
          title: 'Conformité',
          items: [
            {
              label: 'Audit énergétique réglementaire',
              href: '/industrie/operations/audit-reglementaire',
              iconName: 'file-search',
            },
            {
              label: 'Certification ISO 50001',
              href: '/industrie/iso-50001',
              iconName: 'shield-check',
            },
          ],
        },
      ],
    },
    {
      label: 'Aides',
      href: '/industrie/aides',
      iconName: 'coins',
      description: 'Primes CEE, Fonds chaleur, fiscalité énergie',
      megaMenu: [
        {
          title: 'Subventions & primes',
          items: [
            {
              label: 'Primes CEE industrielles',
              href: '/industrie/aides/cee-ind',
              iconName: 'sparkles',
            },
            {
              label: 'Coups de Pouce industriels',
              href: '/industrie/aides/coups-de-pouce',
              iconName: 'flame',
            },
            {
              label: 'Fonds chaleur ADEME',
              href: '/industrie/aides/fonds-chaleur',
              iconName: 'thermometer',
            },
            {
              label: 'Fonds décarbonation',
              href: '/industrie/aides/fonds-decarbonation',
              iconName: 'banknote',
            },
            {
              label: 'Prêts Bpifrance climat',
              href: '/industrie/aides/prets-bpifrance',
              iconName: 'piggy-bank',
            },
          ],
        },
        {
          title: 'Fiscalité & facture',
          items: [
            {
              label: 'Optimisation TURPE (HTA)',
              href: '/industrie/aides/optimisation-turpe',
              iconName: 'trending-down',
            },
            {
              label: 'Exonération CSPE (électro-intensifs)',
              href: '/industrie/aides/exoneration-cspe',
              iconName: 'percent',
            },
          ],
        },
      ],
    },
  ],
  primaryCta: { label: 'Diagnostic CEE industriel', href: '/industrie/diagnostic-cee' },
  footerCtas: [
    {
      label: 'Diagnostic CEE industriel',
      href: '/industrie/diagnostic-cee',
      description: 'Estimation chiffrée, sans engagement',
      variant: 'primary',
    },
    {
      label: 'Prendre rendez-vous',
      href: '/contact?source=industrie',
      description: "Bureau d'études partenaire, confidentialité garantie",
      variant: 'secondary',
    },
  ],
  footerLeadIn:
    "Audit énergétique obligatoire, primes sur vos équipements, subventions cumulables. Avec un bureau d'études thermique sur chaque dossier.",
};

const COLLECTIVITES: AudienceNavConfig = {
  quickLinks: [
    { label: 'Accueil', href: '/collectivites', iconName: 'landmark' },
    {
      label: 'Patrimoine',
      href: '/collectivites/patrimoine',
      iconName: 'building',
      description: 'École, mairie, gymnase, EHPAD, éclairage public',
      megaMenu: [
        {
          title: 'Bâtiments accueillant du public',
          items: [
            {
              label: 'Écoles & groupes scolaires',
              href: '/collectivites/patrimoine/ecoles',
              iconName: 'school',
            },
            {
              label: 'Mairies & hôtels de ville',
              href: '/collectivites/patrimoine/mairies',
              iconName: 'landmark',
            },
            {
              label: 'Gymnases & piscines',
              href: '/collectivites/patrimoine/gymnases',
              iconName: 'activity',
            },
            { label: 'EHPAD & santé', href: '/collectivites/patrimoine/ehpad', iconName: 'heart' },
          ],
        },
        {
          title: 'Espaces & équipements',
          items: [
            {
              label: 'Éclairage public LED',
              href: '/collectivites/patrimoine/eclairage-public',
              iconName: 'sparkles',
            },
            {
              label: 'Réseaux de chaleur',
              href: '/collectivites/patrimoine/reseaux-chaleur',
              iconName: 'flame',
            },
            {
              label: 'Bailleurs sociaux',
              href: '/collectivites/patrimoine/bailleurs-sociaux',
              iconName: 'building-2',
            },
          ],
        },
      ],
    },
    {
      label: 'Aides publiques',
      href: '/collectivites/aides',
      iconName: 'coins',
      description:
        'Fonds vert, dotations État (DETR, DSIL), primes énergie (CEE), récupération TVA',
      megaMenu: [
        {
          title: '',
          items: [
            { label: 'Fonds vert', href: '/collectivites/aides/fonds-vert', iconName: 'sparkles' },
            { label: 'DETR / DSIL', href: '/collectivites/aides/detr-dsil', iconName: 'landmark' },
            {
              label: 'CEE collectivités (ACTEE)',
              href: '/collectivites/aides/cee-collectivites',
              iconName: 'file-text',
            },
            {
              label: 'Banque des Territoires',
              href: '/collectivites/aides/banque-territoires',
              iconName: 'piggy-bank',
            },
            {
              label: 'Fonds chaleur ADEME',
              href: '/collectivites/aides/fonds-chaleur',
              iconName: 'flame',
            },
            {
              label: 'Optimisation TURPE du patrimoine',
              href: '/collectivites/aides/optimisation-turpe',
              iconName: 'trending-down',
            },
          ],
        },
      ],
    },
  ],
  primaryCta: { label: 'Pré-étude offerte', href: '/contact?source=collectivite' },
  footerCtas: [
    {
      label: 'Pré-étude patrimoine',
      href: '/contact?source=collectivite',
      description: 'Conforme commande publique, confidentialité garantie',
      variant: 'primary',
    },
    {
      label: 'Aides cumulables',
      href: '/collectivites/aides',
      description: 'Fonds vert + subventions + primes',
      variant: 'secondary',
    },
  ],
  footerLeadIn:
    'Référencé UGAP. Marchés conformes au code de la commande publique. Pré-étude gratuite, confidentialité totale.',
};

const COPROPRIETES: AudienceNavConfig = {
  quickLinks: [
    { label: 'Accueil', href: '/coproprietes', iconName: 'building' },
    {
      label: 'Travaux copro',
      href: '/coproprietes/travaux',
      iconName: 'wrench',
      description: 'Isolation, chaufferie, ventilation collective',
      megaMenu: [
        {
          title: 'Enveloppe collective',
          items: [
            {
              label: 'Isolation des façades',
              href: '/coproprietes/travaux/isolation-facade',
              iconName: 'shield-check',
            },
            {
              label: 'Isolation toiture',
              href: '/coproprietes/travaux/isolation-toiture',
              iconName: 'home',
            },
            {
              label: 'Fenêtres parties communes',
              href: '/coproprietes/travaux/fenetres',
              iconName: 'layers',
            },
          ],
        },
        {
          title: 'Chauffage & ventilation',
          items: [
            {
              label: 'Chaufferie collective',
              href: '/coproprietes/travaux/chaufferie',
              iconName: 'flame',
            },
            {
              label: 'PAC collective',
              href: '/coproprietes/travaux/pac-collective',
              iconName: 'snowflake',
            },
            {
              label: 'Ventilation collective',
              href: '/coproprietes/travaux/ventilation',
              iconName: 'wind',
            },
          ],
        },
      ],
    },
    {
      label: 'Aides copro',
      href: '/coproprietes/aides',
      iconName: 'coins',
      description: 'MPR Copro, CEE collectifs, éco-PTZ collectif',
      megaMenu: [
        {
          title: '',
          items: [
            {
              label: 'MaPrimeRénov’ Copropriété',
              href: '/coproprietes/aides/mpr-copro',
              iconName: 'piggy-bank',
            },
            {
              label: 'CEE collectifs',
              href: '/coproprietes/aides/cee-collectifs',
              iconName: 'sparkles',
            },
            {
              label: 'Éco-PTZ collectif',
              href: '/coproprietes/aides/eco-ptz-collectif',
              iconName: 'banknote',
            },
            {
              label: 'Aides locales copro',
              href: '/coproprietes/aides/aides-locales',
              iconName: 'landmark',
            },
          ],
        },
      ],
    },
    {
      label: 'Process AG',
      href: '/coproprietes/process-ag',
      iconName: 'clipboard-check',
      description: 'Note de convocation, résolution, vote',
      megaMenu: [
        {
          title: '',
          items: [
            {
              label: 'Note de convocation',
              href: '/coproprietes/process-ag/note-convocation',
              iconName: 'file-text',
            },
            {
              label: 'Projet de résolution',
              href: '/coproprietes/process-ag/resolution',
              iconName: 'file-signature',
            },
            { label: 'Vote en AG', href: '/coproprietes/process-ag/vote', iconName: 'users' },
            {
              label: 'Plan pluriannuel de travaux',
              href: '/coproprietes/process-ag/ppt',
              iconName: 'clipboard-list',
            },
          ],
        },
      ],
    },
  ],
  primaryCta: { label: 'Préparer mon AG', href: '/contact?source=copropriete' },
  footerCtas: [
    {
      label: 'Préparer mon AG',
      href: '/contact?source=copropriete',
      description: 'Note de convocation + projet de résolution',
      variant: 'primary',
    },
    {
      label: 'Calculer les aides Copro',
      href: '/coproprietes/calculer-mes-aides',
      description: 'Toutes les aides collectives cumulées',
      variant: 'secondary',
    },
  ],
  footerLeadIn: 'Présentation en AG offerte sur demande. Vote positif dès la première convocation.',
};

const ARTISANS: AudienceNavConfig = {
  quickLinks: [
    { label: 'Accueil', href: '/artisans', iconName: 'hard-hat' },
    {
      label: 'Catalogue CEE',
      href: '/artisans/catalogue',
      iconName: 'file-text',
      description: 'Fiches CEE par famille + Coups de Pouce',
      megaMenu: [
        {
          title: 'Résidentiel (BAR)',
          items: [
            {
              label: 'Isolation (BAR-EN)',
              href: '/artisans/catalogue/bar-en',
              iconName: 'shield-check',
            },
            {
              label: 'Chauffage & ECS (BAR-TH)',
              href: '/artisans/catalogue/bar-th',
              iconName: 'flame',
            },
            {
              label: 'Coup de pouce chauffage',
              href: '/artisans/catalogue/coup-de-pouce-chauffage',
              iconName: 'sparkles',
            },
          ],
        },
        {
          title: 'Tertiaire (BAT)',
          items: [
            {
              label: 'Chauffage tertiaire (BAT-TH)',
              href: '/artisans/catalogue/bat-th',
              iconName: 'flame',
            },
            {
              label: 'Isolation tertiaire (BAT-EN)',
              href: '/artisans/catalogue/bat-en',
              iconName: 'shield-check',
            },
            {
              label: 'Équipements (BAT-EQ)',
              href: '/artisans/catalogue/bat-eq',
              iconName: 'sparkles',
            },
          ],
        },
        {
          title: 'Catalogue complet',
          items: [
            { label: 'Toutes les fiches CEE', href: '/cee/fiches', iconName: 'layers' },
            {
              label: 'Coups de Pouce actifs',
              href: '/cee?bonification=coup-de-pouce',
              iconName: 'sparkles',
            },
          ],
        },
      ],
    },
    {
      label: 'Mandat & ressources',
      href: '/artisans/ressources',
      iconName: 'briefcase',
      description: 'Process mandataire, CGV, RC Pro',
      megaMenu: [
        {
          title: '',
          items: [
            {
              label: 'Comment ça marche',
              href: '/artisans/ressources/process',
              iconName: 'workflow',
            },
            {
              label: 'Mandat CEE en 5 min',
              href: '/artisans/ressources/mandat',
              iconName: 'file-signature',
            },
            {
              label: 'Tarif des primes du jour',
              href: '/artisans/ressources/tarif-primes',
              iconName: 'trending-up',
            },
            {
              label: 'CGV mandataire CEE',
              href: '/legal/cgv-courtier-energie',
              iconName: 'file-text',
            },
            {
              label: 'RC Pro & conformité',
              href: '/artisans/ressources/conformite',
              iconName: 'shield-check',
            },
          ],
        },
      ],
    },
    {
      label: 'Devenir partenaire',
      href: '/devenir-partenaire/candidater?profile=artisan',
      iconName: 'wallet',
    },
  ],
  primaryCta: {
    label: 'Devenir partenaire',
    href: '/devenir-partenaire/candidater?profile=artisan',
  },
  footerCtas: [
    {
      label: 'Candidater (5 min)',
      href: '/devenir-partenaire/candidater?profile=artisan',
      description: 'Qualification vérifiée, mandat en ligne',
      variant: 'highlight',
    },
    {
      label: 'Lire nos CGV mandataire CEE',
      href: '/legal/cgv-courtier-energie',
      description: 'Zéro avance, sans frais cachés',
      variant: 'secondary',
    },
  ],
  footerLeadIn:
    'Zéro avance, tarif du marché garanti. Vous faites les chantiers, on monte les dossiers.',
};

const AGRICULTEURS: AudienceNavConfig = {
  quickLinks: [
    { label: 'Accueil', href: '/agriculteurs', iconName: 'tractor' },
    {
      label: 'Opérations',
      href: '/agriculteurs/operations',
      iconName: 'wrench',
      description: 'Travaux d’efficacité énergétique éligibles aux CEE et aides agricoles',
      megaMenu: [
        {
          title: 'Serres horticoles',
          items: [
            {
              label: 'Déshumidification',
              href: '/agriculteurs/operations/deshumidification-serre',
              iconName: 'droplet',
            },
            {
              label: 'Régulation climatique',
              href: '/agriculteurs/operations/regulation-climatique',
              iconName: 'gauge',
            },
            {
              label: 'Isolation & écrans thermiques',
              href: '/agriculteurs/operations/isolation-ecrans-serre',
              iconName: 'shield-check',
            },
            {
              label: 'Chauffage serre (biomasse, PAC)',
              href: '/agriculteurs/operations/chauffage-serre',
              iconName: 'flame',
            },
          ],
        },
        {
          title: 'Élevage & froid',
          items: [
            {
              label: 'Isolation des bâtiments',
              href: '/agriculteurs/operations/isolation-elevage',
              iconName: 'home',
            },
            {
              label: 'Ventilation économe',
              href: '/agriculteurs/operations/ventilation-elevage',
              iconName: 'wind',
            },
            {
              label: 'Froid agricole (tank à lait)',
              href: '/agriculteurs/operations/froid-agricole',
              iconName: 'snowflake',
            },
            {
              label: 'Pompe à chaleur élevage',
              href: '/agriculteurs/operations/pac-elevage',
              iconName: 'thermometer',
            },
          ],
        },
        {
          title: 'Énergies & cultures',
          items: [
            {
              label: 'Variateurs de vitesse',
              href: '/agriculteurs/operations/variateurs-vitesse',
              iconName: 'zap',
            },
            {
              label: 'Séchage performant',
              href: '/agriculteurs/operations/sechage-performant',
              iconName: 'sun',
            },
            {
              label: 'Pompe d’irrigation économe',
              href: '/agriculteurs/operations/pompe-irrigation',
              iconName: 'droplet',
            },
            {
              label: 'Méthanisation à la ferme',
              href: '/agriculteurs/operations/methanisation',
              iconName: 'flame',
            },
          ],
        },
      ],
    },
    {
      label: 'Aides',
      href: '/agriculteurs/aides',
      iconName: 'coins',
      description: 'CEE secteur AGRI, FranceAgriMer, PCAE, aides régionales',
      megaMenu: [
        {
          title: 'Aides nationales',
          items: [
            {
              label: 'CEE secteur AGRI',
              href: '/agriculteurs/aides/cee-agricoles',
              iconName: 'sparkles',
            },
            {
              label: 'FranceAgriMer',
              href: '/agriculteurs/aides/franceagrimer',
              iconName: 'landmark',
            },
            {
              label: 'Plan France 2030',
              href: '/agriculteurs/aides/plan-france-2030',
              iconName: 'banknote',
            },
          ],
        },
        {
          title: 'Aides régionales',
          items: [
            {
              label: 'PCAE (FEADER régional)',
              href: '/agriculteurs/aides/pcae',
              iconName: 'tractor',
            },
            {
              label: 'Aides locales & Conseil régional',
              href: '/agriculteurs/aides/aides-locales',
              iconName: 'landmark',
            },
          ],
        },
        {
          title: 'Optimiser la facture',
          items: [
            {
              label: 'Optimisation TURPE (acheminement)',
              href: '/agriculteurs/aides/optimisation-turpe',
              iconName: 'trending-down',
            },
            {
              label: 'Exonération CSPE (accise élec)',
              href: '/agriculteurs/aides/exoneration-cspe',
              iconName: 'percent',
            },
          ],
        },
      ],
    },
    { label: 'Actu', href: '/blog', iconName: 'newspaper' },
  ],
  primaryCta: { label: 'Diagnostic 60s', href: '/agriculteurs/diagnostic-cee' },
  footerCtas: [
    {
      label: 'Diagnostic CEE agricole',
      href: '/agriculteurs/diagnostic-cee',
      description: "Bâtiments d'exploitation, aides régionales",
      variant: 'primary',
    },
    {
      label: 'Estimer les aides de mon exploitation',
      href: '/agriculteurs/primes-cee',
      description: 'Primes + PCAE + FranceAgriMer',
      variant: 'secondary',
    },
  ],
  footerLeadIn:
    'On regarde avec vous les aides accessibles à votre exploitation (CEE agricoles, PCAE de votre Région, FranceAgriMer) — sans jargon, sans engagement.',
};

export const AUDIENCE_NAV: Record<AudienceId, AudienceNavConfig> = {
  particuliers: PARTICULIERS,
  professionnels: PROFESSIONNELS,
  entreprises: ENTREPRISES,
  tertiaire: TERTIAIRE,
  industrie: INDUSTRIE,
  collectivites: COLLECTIVITES,
  coproprietes: COPROPRIETES,
  artisans: ARTISANS,
  agriculteurs: AGRICULTEURS,
};

export function getAudienceNav(id: AudienceId): AudienceNavConfig {
  return AUDIENCE_NAV[id];
}
